import { supabase } from "@/integrations/supabase/client";
import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Language } from "@/locales/translations";
import { plentyOneApi } from "@/services/plentyOneApi";
import { b2bAuthService } from "@/services/auth/b2bAuthService";
import { queryClient } from "@/main";
import { prefetchUserData, checkAndPrefetchUserData } from "@/utils/prefetchUtils";
import { logger } from "@/services/logger";
import { setSentryUser, clearSentryUser } from "@/sentry";
import type { PricingType, PaymentMethodType } from "@/types/b2b";

interface UserSettings {
  language: Language;
  theme?: "light" | "dark";
}

import { ContactAddress, ContactRelation } from "@/types/contact";

interface User {
  id: string;
  email: string;
  name: string;
  company: string;
  role: "customer" | "admin";
  settings: UserSettings;
  // ERP Contact Information
  erpContactId?: number;
  erpAddresses?: ContactAddress[];
  erpContactClassId?: number;
  erpContactClassName?: string;
  // B2B Auth Information
  b2bAuthId?: string;
  // Demo User Flag
  isDemoUser?: boolean;
  // Pricing Information
  pricing_type?: PricingType;
  payment_method_type?: PaymentMethodType;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isPrefetchComplete: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  updateUserSettings: (settings: Partial<UserSettings>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Demo user credentials - in a real app, this would be handled by a backend
const DEMO_USERS: Record<string, { password: string; user: User }> = {
  "demo@knitido.de": {
    password: "demo123",
    user: {
      id: "demo-1",
      email: "demo@knitido.de",
      name: "Demo User",
      company: "Demo Company Ltd.",
      role: "customer",
      settings: {
        language: "ja",
        theme: "light"
      },
      isDemoUser: true,
      erpContactId: 35461,
      erpContactClassId: 1,
      erpContactClassName: 'B2B Tier-1',
      erpAddresses: [
        {
          id: 1,
          typeId: 1,
          name1: "Demo Company Ltd.",
          name2: "Demo User",
          address1: "Muster Straße 123",
          address2: "Suite 456",
          postalCode: "12345",
          town: "Berlin",
          countryId: 1,
          phone: "+49 30 12345678"
        }
      ]
    }
  },
  "admin@knitido.com": {
    password: "admin123",
    user: {
      id: "2",
      email: "admin@knitido.com",
      name: "Admin User",
      company: "Knitido GmbH",
      role: "admin",
      settings: {
        language: "de",
        theme: "light"
      }
    }
  }
};

const SESSION_STORAGE_KEY = "knitido_auth_session";
const SESSION_EXPIRY_KEY = "knitido_auth_expiry";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPrefetchComplete, setIsPrefetchComplete] = useState(false);

  // Handles all post-login logic
  const handleSuccessfulLogin = (userData: User) => {
    // Set session expiry to 8 hours from now
    const expiryTime = Date.now() + (8 * 60 * 60 * 1000);
    
    // Store user data and expiry in session storage
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(userData));
    sessionStorage.setItem(SESSION_EXPIRY_KEY, expiryTime.toString());
    
    setUser(userData);

    // Run prefetch logic
    setIsPrefetchComplete(false);
    prefetchUserData(queryClient, userData)
      .then(() => {
        setIsPrefetchComplete(true);
      })
      .catch(error => {
        logger.error('[useAuth] Prefetch error', { error: error instanceof Error ? error.message : String(error) });
        setIsPrefetchComplete(true); // Treat as complete even on error
      });
  };

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = () => {
      try {
        const storedUser = sessionStorage.getItem(SESSION_STORAGE_KEY);
        const expiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
        
        if (storedUser && expiry) {
          const expiryTime = parseInt(expiry);
          const now = Date.now();
          
          if (now < expiryTime) {
            // Session is still valid
            const userData = JSON.parse(storedUser);
            setUser(userData);
            
            // Restore Sentry user context
            setSentryUser({
              id: userData.id,
              email: userData.email,
              companyName: userData.company,
              contactId: userData.erpContactId?.toString()
            });
            
            // Run prefetch on session restore
            setIsPrefetchComplete(false);
            checkAndPrefetchUserData(queryClient, userData)
              .then(() => {
                setIsPrefetchComplete(true);
              })
              .catch(error => {
                logger.error('[useAuth] Session restore prefetch error', { error: error.message });
                setIsPrefetchComplete(true);
              });
          } else {
            // Session expired, clear storage
            sessionStorage.removeItem(SESSION_STORAGE_KEY);
            sessionStorage.removeItem(SESSION_EXPIRY_KEY);
          }
        }
      } catch (error) {
        logger.error('Error checking session', { error: error.message });
        // Clear potentially corrupted session data
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        sessionStorage.removeItem(SESSION_EXPIRY_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingSession();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    const normalizedEmail = email.toLowerCase().trim();
    
    try {
      // Use the new B2B auth service
      const authResult = await b2bAuthService.login(normalizedEmail, password);
      
      if (!authResult.success) {
        
        // Fallback to demo user for development
        if (password === "demo123") {
          const demoUser = DEMO_USERS[normalizedEmail];
          if (demoUser && demoUser.password === password) {
            handleSuccessfulLogin(demoUser.user);
            return true;
          }
        }
        
        logger.error('[useAuth] Login failed - no valid auth method');
        return false;
      }
      
      // B2B auth successful
      let userData: User;
      
      // Build user data object
      if (authResult.user?.erpContactId || authResult.erpContact) {
        const erpContactId = authResult.user?.erpContactId || authResult.erpContact?.id;
        let erpContact = authResult.erpContact;
        
        if (!erpContact && erpContactId) {
          try {
            erpContact = await plentyOneApi.getContactInfo(erpContactId);
          } catch (error) {
            logger.error('[useAuth] Failed to fetch ERP contact', { error: error.message });
          }
        }
        
        let addresses: ContactAddress[] = erpContact?.addresses || [];
        let companyName = "Unknown Company";
        
        if (erpContact) {
          const contactInfo = erpContact.contactInfo || {};
          if (contactInfo.company?.trim()) {
            companyName = contactInfo.company.trim();
          } else if (addresses.length > 0) {
            const billingAddress = addresses.find((addr: ContactAddress) => 
              addr.contactRelations?.some((rel: ContactRelation) => rel.typeId === 1)
            );
            companyName = billingAddress?.name1?.trim() || addresses.find(a => a.name1?.trim())?.name1?.trim() || companyName;
          } else if (contactInfo.firstName || contactInfo.lastName) {
            const fullName = `${contactInfo.firstName || ''} ${contactInfo.lastName || ''}`.trim();
            if (fullName) companyName = `${fullName} (Individual)`;
          }
        }
        
        let userName = "B2B User";
        if (erpContact) {
          const contactInfo = erpContact.contactInfo || {};
          const fullName = `${contactInfo.firstName || ''} ${contactInfo.lastName || ''}`.trim();
          if (fullName) userName = fullName;
        }

        let contactClassId: number | undefined = erpContact?.contactInfo?.classId;
        let contactClassName: string | undefined;
        
        if (contactClassId) {
          if (contactClassId === 1) contactClassName = 'B2B Tier-1';
          else if (contactClassId === 2) contactClassName = 'B2B Tier-2';
          else {
            try {
              const contactClass = await plentyOneApi.getContactClass(contactClassId);
              if (contactClass?.name) contactClassName = contactClass.name;
            } catch (error) {
              logger.error('[useAuth] Failed to fetch contact class', { error: error.message });
            }
          }
        } else {
          contactClassId = 2;
          contactClassName = 'B2B Tier-2';
        }

        userData = {
          id: authResult.user!.id,
          email: authResult.user!.email,
          name: userName,
          company: companyName,
          role: "customer" as const,
          settings: { language: "ja" as Language, theme: "light" as const },
          erpContactId: erpContactId,
          erpAddresses: addresses,
          erpContactClassId: contactClassId,
          erpContactClassName: contactClassName,
          b2bAuthId: authResult.user!.id,
          pricing_type: authResult.user?.pricingType,
          payment_method_type: authResult.user?.paymentMethodType
        };
      } else {
        userData = {
          id: authResult.user!.id,
          email: authResult.user!.email,
          name: "B2B User",
          company: "Unknown Company",
          role: "customer" as const,
          settings: { language: "ja" as Language, theme: "light" as const },
          erpContactClassId: 2,
          erpContactClassName: 'B2B Tier-2',
          b2bAuthId: authResult.user!.id,
          pricing_type: authResult.user?.pricingType,
          payment_method_type: authResult.user?.paymentMethodType
        };
      }
      
      // Set Sentry user context
      setSentryUser({
        id: userData.id,
        email: userData.email,
        companyName: userData.company,
        contactId: userData.erpContactId?.toString()
      });

      handleSuccessfulLogin(userData);
      return true;
      
    } catch (error) {
      logger.error('[useAuth] Unexpected login error', { error: error.message });
      return false;
    }
  };

  const logout = () => {
    // Log user activity before clearing the session
    if (user && user.b2bAuthId) {
      (async () => {
        try {
          const { logActivity } = await import('@/services/activityLogger');
          await logActivity({
            userId: user.b2bAuthId!,
            actionType: 'USER_LOGOUT',
            details: { source: 'WebApp' }
          });
        } catch (e) {
          logger.error('[useAuth] Exception while logging user logout', { error: e instanceof Error ? e.message : String(e) });
        }
      })();
    }

    setUser(null);
    setIsPrefetchComplete(false);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_EXPIRY_KEY);
    
    // Clear Sentry user context
    clearSentryUser();
  };

  const updateUserSettings = (newSettings: Partial<UserSettings>) => {
    if (!user) return;

    const updatedUser = {
      ...user,
      settings: {
        ...user.settings,
        ...newSettings
      }
    };

    setUser(updatedUser);
    
    try {
      // Update session storage with new user data
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(updatedUser));
    } catch (error) {
      logger.error('Error updating user settings');
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    isPrefetchComplete,
    login,
    logout,
    updateUserSettings
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};